// CAE Dashboard — security-event stream, risk dashboard, CAE actions log,
// threshold config. PAM Epic 10 Task 8.
//
// OIDCPAM Gate-6 Phase 5e: migrated off MUI onto the composite
// @devnw/cyber-iam/iam/CAEDashboard (SecurityEventsPanel + RiskDashboardPanel
// + SecurityEventsPanel-as-actions-log + ThresholdConfigPanel behind an
// accessible TabStrip). The composite owns tab switching, the risk-override
// inline editor, and the threshold save form; this page still owns every API
// call, the wire-mapping (../api/caeMapping — no latent bug found for
// events/risk state; see that file's header for the thresholds PascalCase
// note), tenant gating, and wiring each callback to its mutation.
//
// Behavior note: the composite requires all four tabs' props up front (it
// has no onTabChange escape hatch for the host to detect the active tab), so
// unlike the pre-migration page — which only fetched a tab's data the first
// time it was selected — this page fetches all four data sources on mount.
// The shared admin rate limit (/v1/admin: 5 rps / 30 burst, see
// rate_limits.go) comfortably absorbs four concurrent GETs, so this is a
// documented behavior change, not a functional regression.
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import IamCAEDashboard from '@devnw/cyber-iam/iam/CAEDashboard/CAEDashboard'
import type {
  SecurityEvent as IamSecurityEvent,
  RiskState as IamRiskState,
  RiskThresholds as IamRiskThresholds,
  RiskOverride,
  CaeRiskLevel,
} from '@devnw/cyber-iam/iam/types'
import { useAdminGenClient } from '../api/genClient'
import { useActiveTenant } from '../layout/TenantSwitcher'
import {
  toIamSecurityEvent,
  toIamRiskState,
  toIamRiskThresholds,
} from '../api/caeMapping'

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message : 'Request failed'
}

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('lg')};
`

const MutedText = styled.p`
  margin: 0;
  color: ${({ theme }) => asUnified(theme).text.secondary};
`

export const CAEDashboard = (): JSX.Element => {
  const { tenant } = useActiveTenant()
  const client = useAdminGenClient()

  // ── Security events (tab 0) ────────────────────────────────────────────
  const [events, setEvents] = useState<IamSecurityEvent[]>([])
  const [eventsLoading, setEventsLoading] = useState(false)
  const [eventsError, setEventsError] = useState<string | undefined>(undefined)

  const loadEvents = useCallback(
    async (subjectRef?: string): Promise<void> => {
      if (!tenant) return
      setEventsLoading(true)
      setEventsError(undefined)
      try {
        const data = await client.adminListCAEEvents({
          tenantId: tenant,
          limit: 50,
          ...(subjectRef ? { subjectRef } : {}),
        })
        setEvents((data.events ?? []).map(toIamSecurityEvent))
      } catch (e: unknown) {
        setEventsError(errMessage(e))
      } finally {
        setEventsLoading(false)
      }
    },
    [tenant, client]
  )

  // ── Actions log (tab 2) — same endpoint, no subject filter, larger page ──
  const [actionEvents, setActionEvents] = useState<IamSecurityEvent[]>([])
  const [actionsLoading, setActionsLoading] = useState(false)
  const [actionsError, setActionsError] = useState<string | undefined>(
    undefined
  )

  const loadActions = useCallback(async (): Promise<void> => {
    if (!tenant) return
    setActionsLoading(true)
    setActionsError(undefined)
    try {
      const data = await client.adminListCAEEvents({
        tenantId: tenant,
        limit: 100,
      })
      setActionEvents((data.events ?? []).map(toIamSecurityEvent))
    } catch (e: unknown) {
      setActionsError(errMessage(e))
    } finally {
      setActionsLoading(false)
    }
  }, [tenant, client])

  // ── Risk dashboard (tab 1) ──────────────────────────────────────────────
  const [subjects, setSubjects] = useState<IamRiskState[]>([])
  const [riskLoading, setRiskLoading] = useState(false)
  const [riskError, setRiskError] = useState<string | undefined>(undefined)
  // Tracks the last-selected min level (ref, not state — it's only consulted
  // for the post-override reload, never rendered) so overriding a subject
  // reloads the list with the filter the admin currently has selected.
  const minLevelRef = useRef<CaeRiskLevel>('high')

  const loadRisk = useCallback(
    async (level: CaeRiskLevel): Promise<void> => {
      if (!tenant) return
      setRiskLoading(true)
      setRiskError(undefined)
      try {
        const data = await client.adminListHighRiskSubjects({
          tenantId: tenant,
          minLevel: level,
          limit: 100,
        })
        setSubjects((data.subjects ?? []).map(toIamRiskState))
      } catch (e: unknown) {
        setRiskError(errMessage(e))
      } finally {
        setRiskLoading(false)
      }
    },
    [tenant, client]
  )

  const handleRiskRefresh = useCallback(
    (level: CaeRiskLevel): void => {
      minLevelRef.current = level
      loadRisk(level)
        .then(() => {})
        .catch(() => {})
    },
    [loadRisk]
  )

  const handleOverride = useCallback(
    async (override: RiskOverride): Promise<void> => {
      if (!tenant) return
      await client.adminOverrideCAERisk({
        tenantId: tenant,
        subjectId: override.subjectRef,
        level: override.level,
        reason: override.reason,
      })
      await loadRisk(minLevelRef.current)
    },
    [tenant, client, loadRisk]
  )

  // ── Threshold config (tab 3) ────────────────────────────────────────────
  const [thresholds, setThresholds] = useState<IamRiskThresholds | null>(null)
  const [thresholdsLoading, setThresholdsLoading] = useState(false)
  const [thresholdsError, setThresholdsError] = useState<string | undefined>(
    undefined
  )

  const loadThresholds = useCallback(async (): Promise<void> => {
    if (!tenant) return
    setThresholdsLoading(true)
    setThresholdsError(undefined)
    try {
      const data = await client.adminGetCAEConfig({ tenantId: tenant })
      setThresholds(
        toIamRiskThresholds((data.thresholds ?? {}) as Record<string, unknown>)
      )
    } catch (e: unknown) {
      setThresholdsError(errMessage(e))
    } finally {
      setThresholdsLoading(false)
    }
  }, [tenant, client])

  const handleSaveThresholds = useCallback(
    async (next: IamRiskThresholds): Promise<void> => {
      if (!tenant) return
      await client.adminUpdateCAEConfig({
        tenantId: tenant,
        impossibleTravelKmCritical: next.impossibleTravelKmCritical,
        failedAuthBurstHigh: next.failedAuthBurstHigh,
        failedAuthBurstCritical: next.failedAuthBurstCritical,
        velocityAnomalyHigh: next.velocityAnomalyHigh,
        velocityAnomalyCritical: next.velocityAnomalyCritical,
        sensitiveActionRateHigh: next.sensitiveActionRateHigh,
      })
      setThresholds(next)
    },
    [tenant, client]
  )

  useEffect(() => {
    loadEvents()
      .then(() => {})
      .catch(() => {})
    loadRisk(minLevelRef.current)
      .then(() => {})
      .catch(() => {})
    loadActions()
      .then(() => {})
      .catch(() => {})
    loadThresholds()
      .then(() => {})
      .catch(() => {})
  }, [loadEvents, loadRisk, loadActions, loadThresholds])

  if (!tenant) {
    return (
      <PageContainer>
        <MutedText>Select a tenant to view the CAE dashboard.</MutedText>
      </PageContainer>
    )
  }

  return (
    <IamCAEDashboard
      testID="cae-dashboard"
      securityEventsProps={{
        events,
        onRefresh: (subjectRef) => {
          loadEvents(subjectRef)
            .then(() => {})
            .catch(() => {})
        },
        loading: eventsLoading,
        error: eventsError,
        testID: 'cae-events',
      }}
      actionsLogProps={{
        events: actionEvents,
        onRefresh: () => {
          loadActions()
            .then(() => {})
            .catch(() => {})
        },
        loading: actionsLoading,
        error: actionsError,
        testID: 'cae-actions',
      }}
      riskProps={{
        subjects,
        onRefresh: handleRiskRefresh,
        defaultMinLevel: minLevelRef.current,
        onOverride: handleOverride,
        loading: riskLoading,
        error: riskError,
        testID: 'cae-risk',
      }}
      thresholdProps={{
        thresholds,
        onSave: handleSaveThresholds,
        loading: thresholdsLoading,
        error: thresholdsError,
        testID: 'cae-thresholds',
      }}
    />
  )
}
